import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { jsPDF } from 'jspdf';
import { paymentsAPI, installmentsAPI, feeTypesAPI, schoolLogoAPI, settingsAPI, hostelFeeTemplatesAPI, studentsAPI } from '../services/api';
import { useYear } from '../context/YearContext';
import { QRCodeCanvas as QRCode } from "qrcode.react";
import {
  FULLY_PAID_MESSAGE,
  getStudentPendingAmount,
  isStudentFullyPaid,
} from '../utils/studentFeeStatus';
const today = () => new Date().toISOString().split('T')[0];

const escHtml = (str) => String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const EMPTY_FORM = {
  payment_type:  'monthly',
  amount:        '',
  payment_date:  today(),
  month_year:    '',
  term:          '',
  notes:         '',
  payment_mode:  '',
};

const TUITION_BUCKET_PAYMENT_TYPES = new Set(['monthly', 'term', 'other', 'admission']);

const accumulatePaidBuckets = (payments = []) =>
  payments.reduce((acc, p) => {
    const type = p.payment_type;
    const amount = parseFloat(p.amount || 0) || 0;

    if (type === 'bus' || type === 'bus_term1' || type === 'bus_term2') {
      acc.bus       = (acc.bus       || 0) + amount;
      acc[type]     = (acc[type]     || 0) + amount;
    } else if (type === 'tuition_term1' || type === 'tuition_term2' || type === 'tuition_term3') {
      acc[type] = (acc[type] || 0) + amount;
    } else if (type === 'uniform' || type === 'books') {
      acc[type] = (acc[type] || 0) + amount;
    } else if (type === 'hostel_term1') {
      acc.hostel_term1 = (acc.hostel_term1 || 0) + amount;
      acc.hostel       = (acc.hostel       || 0) + amount;
    } else if (type === 'hostel_term2') {
      acc.hostel_term2 = (acc.hostel_term2 || 0) + amount;
      acc.hostel       = (acc.hostel       || 0) + amount;
    } else if (type === 'hostel') {
      acc.hostel = (acc.hostel || 0) + amount;
    } else if (type === 'hostel_term1' || type === 'hostel_term2') {
      acc[type] = (acc[type] || 0) + amount;
    } else if (TUITION_BUCKET_PAYMENT_TYPES.has(type)) {
      acc.tuition = (acc.tuition || 0) + amount;
    }

    return acc;
  }, { bus: 0, bus_term1: 0, bus_term2: 0, tuition_term1: 0, tuition_term2: 0, tuition_term3: 0, uniform: 0, books: 0, tuition: 0, hostel: 0, hostel_term1: 0, hostel_term2: 0 });

export default function PaymentModal({ student, schoolLogoUrl = '', onClose, onSave, initialTab = 'add', historyOnly = false }) {
  const { year }                          = useYear();
  const [activeTab,    setActiveTab]    = useState(initialTab);
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [payments,     setPayments]     = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [lastPayment,   setLastPayment]   = useState(null);
  const [receiptNo,     setReceiptNo]     = useState('');
  const [receiptDueAmount, setReceiptDueAmount] = useState(0);
  const [smsStatus,     setSmsStatus]     = useState(null); // { ok, skipped, error } from server
  const [cancelLoading, setCancelLoading] = useState(false);
  const [installments, setInstallments] = useState([]);
  const [instForm,     setInstForm]     = useState({ term_name: '', expected_amount: '', due_date: '' });
  const [instLoading,  setInstLoading]  = useState(false);
  const [editInstId,   setEditInstId]   = useState(null);
  const [feeTypes,       setFeeTypes]       = useState([]);
  const [multiSelections, setMultiSelections] = useState([]);
  const [multiDate,       setMultiDate]       = useState(today());
  const [multiNotes,      setMultiNotes]      = useState('');
  const [multiLoading,    setMultiLoading]    = useState(false);
  const [multiError,      setMultiError]      = useState(null);
  const [multiDone,       setMultiDone]       = useState(null);
  const [monthlyMonths,   setMonthlyMonths]   = useState(11);
  const [loadedLogoUrl, setLoadedLogoUrl] = useState(() => schoolLogoUrl || sessionStorage.getItem('gs_school_logo_url') || '');
  const [derivedHostelTerms, setDerivedHostelTerms] = useState({ term1: 0, term2: 0 });
  const schoolName     = sessionStorage.getItem('gs_school_name')     || 'School';
  const schoolLocation = sessionStorage.getItem('gs_school_location') || 'Fee Management';
  const receiptRef = useRef();
  const fullyPaid = isStudentFullyPaid(student);
  const pendingAmount = getStudentPendingAmount(student);
  const customPending = multiSelections.filter((s) => s.isCustom).reduce((sum, s) => sum + (s.pending !== null && s.pending > 0 ? s.pending : 0), 0);
  const totalDueAmount = pendingAmount + customPending;
  const effectiveLogoUrl = schoolLogoUrl || loadedLogoUrl;
  const receiptLogoStyle = { display: 'block', maxWidth: 70, maxHeight: 70, objectFit: 'contain', margin: '0 auto 8px' };
  const receiptLogoHtml = effectiveLogoUrl
    ? `<img src="${escHtml(effectiveLogoUrl)}" alt="School Logo" style="display:block;max-width:70px;max-height:70px;object-fit:contain;margin:0 auto 8px;" />`
    : '';

  useEffect(() => {
    setActiveTab(historyOnly ? 'history' : initialTab);
  }, [historyOnly, initialTab, student.student_id]);

  useEffect(() => {
    settingsAPI.getAll()
      .then((r) => {
        const val = parseInt(r.data.data?.monthly_months, 10);
        if (!isNaN(val) && val >= 1 && val <= 12) setMonthlyMonths(val);
      })
      .catch(() => {}); // silent — keep default 11
  }, []);

  // For existing students whose hostel_fee_term1/term2 are 0, derive from template
  useEffect(() => {
    const hostelFee = parseFloat(student.hostel_fee || 0);
    const term1OnStudent = parseFloat(student.hostel_fee_term1 || 0);
    const term2OnStudent = parseFloat(student.hostel_fee_term2 || 0);
    if (hostelFee <= 0 || term1OnStudent > 0 || term2OnStudent > 0) return;
    const hostelType = (student.hostel_types || '').toLowerCase();
    let templateName = null;
    if (hostelType.includes('ac_room') && !hostelType.includes('non')) templateName = 'AC Hostel';
    else if (hostelType.includes('non_ac_room') || hostelType.includes('non')) templateName = 'Non AC Hostel';
    if (!templateName) return;
    hostelFeeTemplatesAPI.getAll(year)
      .then((r) => {
        const rows = r.data.data || [];
        const match = rows.find((row) => (row.fee_name || '').trim() === templateName);
        if (match) {
          setDerivedHostelTerms({
            term1: parseFloat(match.fee_term1 || 0),
            term2: parseFloat(match.fee_term2 || 0),
          });
        }
      })
      .catch(() => {});
  }, [student.student_id, student.hostel_fee, student.hostel_fee_term1, student.hostel_fee_term2, student.hostel_types, year]);

  useEffect(() => {
    if (schoolLogoUrl) {
      setLoadedLogoUrl(schoolLogoUrl);
      return;
    }

    const storedLogoUrl = sessionStorage.getItem('gs_school_logo_url') || '';
    if (storedLogoUrl) {
      setLoadedLogoUrl(storedLogoUrl);
      return;
    }

    let cancelled = false;
    schoolLogoAPI.getCurrent()
      .then((res) => {
        if (cancelled || !res.data?.data?.exists) return;
        const url = schoolLogoAPI.resolveUrl(res.data.data.logo_url || '');
        if (url) {
          sessionStorage.setItem('gs_school_logo_url', url);
          setLoadedLogoUrl(url);
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [schoolLogoUrl]);

  const fetchInstallments = useCallback(async () => {
    try {
      const res = await installmentsAPI.getByStudent(student.student_id, year);
      setInstallments(res.data.data);
    } catch { /* non-critical */ }
  }, [student.student_id, year]);

  const fetchPayments = useCallback(async () => {
    try {
      const res = await paymentsAPI.getByStudent(student.student_id);
      setPayments(res.data.data);
    } catch {
      // Non-critical — history tab will show empty
    }
  }, [student.student_id]);

  useEffect(() => { fetchPayments(); fetchInstallments(); }, [fetchPayments, fetchInstallments]);

  // Initialise multi-pay rows whenever payment history or student changes
  useEffect(() => {
    const paid = accumulatePaidBuckets(payments);

    const rows = [];
    const tuiT1 = parseFloat(student.tuition_fee_term1 || 0);
    const tuiT2 = parseFloat(student.tuition_fee_term2 || 0);
    const tuiT3 = parseFloat(student.tuition_fee_term3 || 0);
    const hasTuiTerms = tuiT1 > 0 || tuiT2 > 0 || tuiT3 > 0;
    if (hasTuiTerms) {
      if (tuiT1 > 0) {
        const pending = Math.max(0, tuiT1 - (paid.tuition_term1 || 0));
        rows.push({ id: 'tuition_term1', label: 'Tuition – Term 1', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0 });
      }
      if (tuiT2 > 0) {
        const pending = Math.max(0, tuiT2 - (paid.tuition_term2 || 0));
        rows.push({ id: 'tuition_term2', label: 'Tuition – Term 2', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0 });
      }
      if (tuiT3 > 0) {
        const pending = Math.max(0, tuiT3 - (paid.tuition_term3 || 0));
        rows.push({ id: 'tuition_term3', label: 'Tuition – Term 3', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0 });
      }
    } else {
      const totalFee = parseFloat(student.total_fee || 0);
      if (totalFee > 0) {
        const pending = Math.max(0, totalFee - paid.tuition);
        rows.push({ id: 'tuition', label: 'Tuition', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0, subType: 'monthly', month_year: '', term: '' });
      }
    }
    const busTerm1 = parseFloat(student.bus_fee_term1 || 0);
    const busTerm2 = parseFloat(student.bus_fee_term2 || 0);
    const hasBusTerms = busTerm1 > 0 || busTerm2 > 0;
    // Generic 'bus' payments fill term1 first then term2 (same logic as fee breakdown)
    let _mGeneric = paid.bus;
    let _mPaidT1 = paid.bus_term1;
    let _mPaidT2 = paid.bus_term2;
    if (hasBusTerms && _mGeneric > 0) {
      const _mT1Extra = Math.min(_mGeneric, Math.max(0, busTerm1 - _mPaidT1));
      _mGeneric -= _mT1Extra;
      _mPaidT1 += _mT1Extra;
      _mPaidT2 += _mGeneric;
    }
    if (hasBusTerms) {
      if (busTerm1 > 0) {
        const pending = Math.max(0, busTerm1 - _mPaidT1);
        rows.push({ id: 'bus_term1', label: 'Transport Fee – Term 1', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0 });
      }
      if (busTerm2 > 0) {
        const pending = Math.max(0, busTerm2 - _mPaidT2);
        rows.push({ id: 'bus_term2', label: 'Transport Fee – Term 2', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0 });
      }
    } else {
      const busFee = parseFloat(student.bus_fee || 0);
      if (busFee > 0) {
        const pending = Math.max(0, busFee - paid.bus);
        rows.push({ id: 'bus', label: 'Transport Fee', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0 });
      }
    }
    const uniformFee = parseFloat(student.uniform_fee || 0);
    if (uniformFee > 0) {
      const pending = Math.max(0, uniformFee - paid.uniform);
      rows.push({ id: 'uniform', label: 'Uniform Fee', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0 });
    }
    const booksFee = parseFloat(student.books_fee || 0);
    if (booksFee > 0) {
      const pending = Math.max(0, booksFee - paid.books);
      rows.push({ id: 'books', label: 'Books Fee', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0 });
    }
    const hostelFee = parseFloat(student.hostel_fee || 0);
    const hostelTerm1Fee = parseFloat(student.hostel_fee_term1 || 0) || derivedHostelTerms.term1;
    const hostelTerm2Fee = parseFloat(student.hostel_fee_term2 || 0) || derivedHostelTerms.term2;
    const hasHostelTerms = hostelTerm1Fee > 0 || hostelTerm2Fee > 0;
    if (hasHostelTerms) {
      // Generic 'hostel' payments fill term1 first then term2
      let _hGeneric = paid.hostel - (paid.hostel_term1 || 0) - (paid.hostel_term2 || 0);
      _hGeneric = Math.max(0, _hGeneric);
      let _hPaidT1 = paid.hostel_term1 || 0;
      let _hPaidT2 = paid.hostel_term2 || 0;
      if (_hGeneric > 0) {
        const _hT1Extra = Math.min(_hGeneric, Math.max(0, hostelTerm1Fee - _hPaidT1));
        _hGeneric -= _hT1Extra;
        _hPaidT1 += _hT1Extra;
        _hPaidT2 += _hGeneric;
      }
      if (hostelTerm1Fee > 0) {
        const pending = Math.max(0, hostelTerm1Fee - _hPaidT1);
        rows.push({ id: 'hostel_term1', label: 'Hostel Fee – Term 1', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0 });
      }
      if (hostelTerm2Fee > 0) {
        const pending = Math.max(0, hostelTerm2Fee - _hPaidT2);
        rows.push({ id: 'hostel_term2', label: 'Hostel Fee – Term 2', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0 });
      }
    } else if (hostelFee > 0) {
      const pending = Math.max(0, hostelFee - (paid.hostel || 0));
      rows.push({ id: 'hostel', label: 'Hostel Fee', pending, amount: pending > 0 ? String(pending) : '', checked: pending > 0 });
    }
    // Custom fee types (lab fee, sports fee, etc.) — capped by fee_amount if set
    feeTypes.forEach((ft) => {
      const paidSoFar = payments.reduce((sum, p) => p.payment_type === ft.fee_name ? sum + parseFloat(p.amount || 0) : sum, 0);
      const cap = parseFloat(ft.fee_amount || 0);
      const pending = cap > 0 ? Math.max(0, cap - paidSoFar) : null; // null = no cap
      rows.push({
        id: ft.fee_name,
        label: ft.fee_name,
        pending,
        paidSoFar,
        amount: (cap > 0 && pending > 0) ? String(pending) : '',
        checked: cap > 0 && pending > 0,
        isCustom: true,
        cap,
      });
    });
    setMultiSelections(rows);
  }, [payments, student, feeTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (year) feeTypesAPI.getAll(year).then((r) => setFeeTypes(r.data.data || [])).catch(() => {});
  }, [year]);

  // Auto-fill monthly amount + current month when modal opens with Monthly selected
  useEffect(() => {
    if (activeTab === 'add' && form.payment_type === 'monthly' && !form.amount) {
      const instalment = Math.round(parseFloat(student.total_fee || 0) / monthlyMonths);
      const d = new Date();
      const monthLabel = d.toLocaleString('en-IN', { month: 'short' }) + ' ' + d.getFullYear();
      setForm((p) => ({
        ...p,
        amount:     p.amount     || (instalment > 0 ? String(instalment) : ''),
        month_year: p.month_year || monthLabel,
      }));
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // helper: human-readable label for payment type
  const paymentLabel = (type) => {
    if (type === 'monthly')        return 'Monthly Tuition';
    if (type === 'term')           return 'Term-wise Tuition';
    if (type === 'old_due')        return 'Old Due (Prev. Year)';
    if (type === 'bus')            return 'Transport Fee';
    if (type === 'bus_term1')      return 'Transport Fee – Term 1';
    if (type === 'bus_term2')      return 'Transport Fee – Term 2';
    if (type === 'tuition_term1')  return 'Tuition – Term 1';
    if (type === 'tuition_term2')  return 'Tuition – Term 2';
    if (type === 'tuition_term3')  return 'Tuition – Term 3';
    if (type === 'uniform')        return 'Uniform Fee';
    if (type === 'books')          return 'Books Fee';
    if (type === 'hostel')         return 'Hostel Fee';
    if (type === 'hostel_term1')   return 'Hostel Fee – Term 1';
    if (type === 'hostel_term2')   return 'Hostel Fee – Term 2';
    if (type === 'other')          return 'Other Fee';
    return type || 'Payment';
  };

  // ── Installment helpers ──────────────────────────────────────────────────────
  const handleInstChange = (e) =>
    setInstForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleInstSubmit = async (e) => {
    e.preventDefault();
    setInstLoading(true);
    try {
      if (editInstId) {
        await installmentsAPI.update(editInstId, instForm);
        setEditInstId(null);
      } else {
        await installmentsAPI.create({ ...instForm, student_id: student.student_id, academic_year: year });
      }
      setInstForm({ term_name: '', expected_amount: '', due_date: '' });
      fetchInstallments();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save installment');
    } finally {
      setInstLoading(false);
    }
  };

  const startEditInst = (inst) => {
    setEditInstId(inst.installment_id);
    setInstForm({ term_name: inst.term_name, expected_amount: inst.expected_amount, due_date: inst.due_date || '' });
  };

  const handleDeleteInst = async (id) => {
    if (!window.confirm('Delete this installment?')) return;
    try {
      await installmentsAPI.delete(id);
      fetchInstallments();
    } catch { alert('Failed to delete installment.'); }
  };

  // Compute how much of each installment is paid by matching term name payments
  const paidByTerm = payments.reduce((acc, p) => {
    if (p.term) acc[p.term] = (acc[p.term] || 0) + parseFloat(p.amount);
    return acc;
  }, {});

  const MONTHLY_MONTHS = monthlyMonths;
  const monthlyInstalment = Math.round(parseFloat(student.total_fee || 0) / MONTHLY_MONTHS);

  const currentMonthLabel = (() => {
    const d = new Date();
    return d.toLocaleString('en-IN', { month: 'short' }) + ' ' + d.getFullYear();
  })();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => {
      const updated = { ...p, [name]: value };
      if (name === 'payment_type') {
        if (value === 'monthly') {
          updated.amount     = String(monthlyInstalment);
          updated.month_year = p.month_year || currentMonthLabel;
        } else {
          // Auto-fill pending amount for fee types that have a cap
          const cap = feeCapMap[value];
          if (cap && cap.fee > 0) {
            const pending = Math.max(0, cap.fee - cap.paid);
            updated.amount = pending > 0 ? String(pending) : '';
          } else {
            updated.amount = '';
          }
        }
      }
      return updated;
    });
  };

  // Calculate per-type paid amounts from actual payments history (source of truth)
  // This avoids stale/out-of-sync DB columns (paid_bus_fee etc.)
  const paidFromHistory = accumulatePaidBuckets(payments);

  // When student has term-based bus fees, generic 'bus' payments fill term1 first then term2
  const _busTerm1Fee = parseFloat(student.bus_fee_term1 || 0);
  const _busTerm2Fee = parseFloat(student.bus_fee_term2 || 0);
  const _hasBusTerms = _busTerm1Fee > 0 || _busTerm2Fee > 0;
  let _effectivePaidBusTerm1 = paidFromHistory.bus_term1;
  let _effectivePaidBusTerm2 = paidFromHistory.bus_term2;
  if (_hasBusTerms && paidFromHistory.bus > 0) {
    let _generic = paidFromHistory.bus;
    const _t1Extra = Math.min(_generic, Math.max(0, _busTerm1Fee - _effectivePaidBusTerm1));
    _generic -= _t1Extra;
    _effectivePaidBusTerm1 += _t1Extra;
    _effectivePaidBusTerm2 += _generic;
  }

  const _tuiTerm1Fee = parseFloat(student.tuition_fee_term1 || 0);
  const _tuiTerm2Fee = parseFloat(student.tuition_fee_term2 || 0);
  const _tuiTerm3Fee = parseFloat(student.tuition_fee_term3 || 0);
  const _hasTuiTerms = _tuiTerm1Fee > 0 || _tuiTerm2Fee > 0 || _tuiTerm3Fee > 0;

  // When student has term-split tuition, distribute generic monthly/term payments into term
  // buckets (term1 first, overflow to term2, then term3) — mirrors progressCard.js logic.
  let _effectivePaidTuiTerm1 = paidFromHistory.tuition_term1;
  let _effectivePaidTuiTerm2 = paidFromHistory.tuition_term2;
  let _effectivePaidTuiTerm3 = paidFromHistory.tuition_term3;
  if (_hasTuiTerms && paidFromHistory.tuition > 0) {
    let _generic = paidFromHistory.tuition;
    const _tt1Extra = Math.min(_generic, Math.max(0, _tuiTerm1Fee - _effectivePaidTuiTerm1));
    _generic -= _tt1Extra;
    const _tt2Extra = Math.min(_generic, Math.max(0, _tuiTerm2Fee - _effectivePaidTuiTerm2));
    _generic -= _tt2Extra;
    const _tt3Extra = _generic;
    _effectivePaidTuiTerm1 += _tt1Extra;
    _effectivePaidTuiTerm2 += _tt2Extra;
    _effectivePaidTuiTerm3 += _tt3Extra;
  }

  // Total tuition charged and paid (for monthly/term cap when no per-term split exists)
  const _totalTuitionFee  = parseFloat(student.total_fee || 0);
  const _totalTuitionPaid = _hasTuiTerms
    ? (_effectivePaidTuiTerm1 + _effectivePaidTuiTerm2 + _effectivePaidTuiTerm3)
    : paidFromHistory.tuition;

  // Map payment type → fee cap (pending amount) for bus/uniform/books + custom fees
  const feeCapMap = {
    monthly:        { fee: _totalTuitionFee, paid: _totalTuitionPaid, label: 'Tuition' },
    term:           { fee: _totalTuitionFee, paid: _totalTuitionPaid, label: 'Tuition' },
    bus:            { fee: parseFloat(student.bus_fee      || 0), paid: paidFromHistory.bus      },
    bus_term1:      { fee: _busTerm1Fee, paid: _effectivePaidBusTerm1 },
    bus_term2:      { fee: _busTerm2Fee, paid: _effectivePaidBusTerm2 },
    tuition_term1:  { fee: _tuiTerm1Fee, paid: _effectivePaidTuiTerm1 },
    tuition_term2:  { fee: _tuiTerm2Fee, paid: _effectivePaidTuiTerm2 },
    tuition_term3:  { fee: _tuiTerm3Fee, paid: _effectivePaidTuiTerm3 },
    uniform:        { fee: parseFloat(student.uniform_fee  || 0), paid: paidFromHistory.uniform  },
    books:          { fee: parseFloat(student.books_fee    || 0), paid: paidFromHistory.books    },
    hostel:         { fee: parseFloat(student.hostel_fee        || 0), paid: paidFromHistory.hostel                },
    hostel_term1:   { fee: parseFloat(student.hostel_fee_term1  || 0) || derivedHostelTerms.term1, paid: paidFromHistory.hostel_term1 || 0 },
    hostel_term2:   { fee: parseFloat(student.hostel_fee_term2  || 0) || derivedHostelTerms.term2, paid: paidFromHistory.hostel_term2 || 0 },
    // custom fee types with a defined amount
    ...Object.fromEntries(
      feeTypes
        .filter((ft) => parseFloat(ft.fee_amount || 0) > 0)
        .map((ft) => [
          ft.fee_name,
          {
            fee:   parseFloat(ft.fee_amount),
            paid:  payments.reduce((s, p) => p.payment_type === ft.fee_name ? s + parseFloat(p.amount || 0) : s, 0),
            label: ft.fee_name,
          },
        ])
    ),
  };

  // Max allowed for current payment type (null = no cap for tuition/other)
  const maxAllowed = (() => {
    const cap = feeCapMap[form.payment_type];
    if (!cap || cap.fee <= 0) return null;
    return Math.max(0, cap.fee - cap.paid);
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    if (fullyPaid) {
      setError(FULLY_PAID_MESSAGE);
      alert(FULLY_PAID_MESSAGE);
      setLoading(false);
      return;
    }
    // Guard: block if fee type is already fully paid or amount exceeds pending
    if (feeCapMap[form.payment_type]) {
      const { fee, paid, label: capLabel } = feeCapMap[form.payment_type];
      const label = capLabel || (form.payment_type === 'bus' ? 'Bus' : form.payment_type === 'uniform' ? 'Uniform' : 'Books');
      const pending = Math.max(0, fee - paid);
      if (fee > 0 && pending <= 0) {
        setError(`${label} fee is already fully paid (₹${fee.toLocaleString('en-IN')}). No amount is pending.`);
        setLoading(false);
        return;
      }
      if (fee > 0 && parseFloat(form.amount) > pending) {
        setError(`Amount cannot exceed the pending ${label} fee of ₹${pending.toLocaleString('en-IN')}.`);
        setLoading(false);
        return;
      }
    }
    try {
      const res = await paymentsAPI.create({ ...form, student_id: student.student_id, academic_year: student.academic_year || year });
      const rno = res.data?.data?.receipt_no || '';
      const paidNow = parseFloat(form.amount) || 0;
      setLastPayment({ ...form });
      setReceiptDueAmount(Math.max(0, totalDueAmount - paidNow));
      setReceiptNo(rno);
      setSmsStatus(res.data?.sms || null);
      setForm(EMPTY_FORM);
      fetchPayments();
      window.dispatchEvent(new CustomEvent('payment-recorded'));
      onSave();
      setActiveTab('receipt');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Remove this payment? The paid amount will be reversed.')) return;
    try {
      await paymentsAPI.delete(paymentId);
      fetchPayments();
      window.dispatchEvent(new CustomEvent('payment-recorded'));
      onSave();
    } catch {
      alert('Failed to delete payment.');
    }
  };

  const handleCancelReceipt = async () => {
    if (!receiptNo) return;
    if (!window.confirm(
      'Cancel this payment?\n\nThis will completely erase the payment record everywhere and restore the fee balance to what it was before. This cannot be undone.'
    )) return;
    setCancelLoading(true);
    try {
      await paymentsAPI.deleteByReceipt(receiptNo);
      setLastPayment(null);
      setReceiptNo('');
      setReceiptDueAmount(0);
      setSmsStatus(null);
      fetchPayments();
      window.dispatchEvent(new CustomEvent('payment-recorded'));
      onSave();
      setActiveTab('add');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel payment.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleMultiSubmit = async (e) => {
    e.preventDefault();
    if (fullyPaid) {
      setMultiError(FULLY_PAID_MESSAGE);
      alert(FULLY_PAID_MESSAGE);
      return;
    }
    const selected = multiSelections.filter((s) => s.isCustom && s.checked && parseFloat(s.amount) > 0);
    if (selected.length === 0) { setMultiError('Select at least one fee type with an amount.'); return; }
    setMultiLoading(true);
    setMultiError(null);
    const receipts = [];
    try {
      for (const s of selected) {
        const payType = s.id === 'tuition' ? s.subType : s.id;
        const payload = {
          student_id:   student.student_id,
          payment_type: payType,
          amount:       parseFloat(s.amount),
          payment_date: multiDate,
          month_year:   payType === 'monthly' ? (s.month_year || null) : null,
          term:         payType === 'term'    ? (s.term || null) : null,
          notes:        multiNotes || null,
          academic_year: student.academic_year || year,
        };
        const res = await paymentsAPI.create(payload);
        receipts.push({ type: s.label + (payType === 'monthly' && s.month_year ? ` (${s.month_year})` : payType === 'term' && s.term ? ` (${s.term})` : ''), amount: s.amount, receipt_no: res.data?.data?.receipt_no || '' });
      }
      setMultiDone(receipts);
      fetchPayments();
      window.dispatchEvent(new CustomEvent('payment-recorded'));
      onSave();
    } catch (err) {
      setMultiError(err.response?.data?.message || 'Failed to record one or more payments');
    } finally {
      setMultiLoading(false);
    }
  };

  const totalPaid = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);

  // ── Old Due Priority ────────────────────────────────────────────────────────
  // old_due_amount on the student record is the live remaining balance
  const remainingOldDue = Math.max(0, parseFloat(student.old_due_amount || 0));

  // jsPDF helvetica cannot render ₹ — use "Rs." for all PDF text
  const fmtPdf = (n) =>
    'Rs. ' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);

  // ── Build a PDF receipt — A4 landscape, two copies side by side ──────────
  const buildReceiptPDF = (opts, qrDataUrl, logoDataUrl, parentQrDataUrl) => {
    // opts: { student_name, father_name, class, village, payment_date,
    //         receipt_no, payment_type, month_year, term, notes, amount, old_due }
    const doc  = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
    const PW   = doc.internal.pageSize.getWidth();   // 297mm
    const PH   = doc.internal.pageSize.getHeight();  // 210mm
    const half = PW / 2;                             // 148.5mm — width of one copy
    const blue  = [13, 36, 68];
    const green = [30, 126, 52];
    const red   = [185, 28, 28];
    const grey  = [102, 102, 102];
    const margin = 8;

    const drawCopy = (xOff, copyLabel) => {
      const W = half - 2; // usable width per copy

      // Copy label top-right
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(160, 160, 160);
      doc.text(copyLabel, xOff + W - margin, 6, { align: 'right' });

      const headerHeight = logoDataUrl ? 28 : 16;
      const pillY = 8 + headerHeight + 3;

      // School header bar
      doc.setFillColor(...blue);
      doc.rect(xOff, 8, W, headerHeight, 'F');
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, 'PNG', xOff + W / 2 - 6, 10, 12, 12);
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(schoolName.toUpperCase(), xOff + W / 2, logoDataUrl ? 26 : 15, { align: 'center' });
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text(`${schoolLocation} | Fee Management`, xOff + W / 2, logoDataUrl ? 32 : 21, { align: 'center' });

      // FEE RECEIPT pill
      doc.setFillColor(26, 60, 110);
      doc.roundedRect(xOff + W / 2 - 18, pillY, 36, 6, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(255, 255, 255);
      doc.text('FEE RECEIPT', xOff + W / 2, pillY + 4.2, { align: 'center' });

      let y = pillY + 13;
      const lx = xOff + margin;
      const rx = xOff + W - margin;

      const row = (label, value, valueColor = [20, 20, 20]) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...grey);
        doc.text(label, lx, y);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...valueColor);
        doc.text(String(value), rx, y, { align: 'right' });
        y += 6;
      };
      const divider = () => {
        doc.setDrawColor(210, 210, 210);
        doc.setLineDash([1, 1], 0);
        doc.line(lx, y - 1, rx, y - 1);
        doc.setLineDash([], 0);
        y += 2;
      };

      row('Student Name', opts.student_name);
      row('Father Name',  opts.father_name || '—');
      if (opts.mobile_number) row('Parent Mobile', opts.mobile_number);
      row('Class',        opts.class);
      row('Village',      opts.village);
      if (opts.admission_no) row('Admission No', opts.admission_no);
      divider();
      row('Payment Date', new Date(opts.payment_date).toLocaleDateString('en-IN'));
      if (opts.receipt_no) row('Receipt No', opts.receipt_no, [26, 86, 219]);
      row('Payment Type', paymentLabel(opts.payment_type));
      if (opts.payment_mode) row('Payment Mode', opts.payment_mode);
      if (opts.transaction_id) row('Transaction ID', opts.transaction_id);
      if (opts.month_year) row('Period', opts.month_year);
      if (opts.term)       row('Term',   opts.term);
      if (opts.notes)      row('Notes',  opts.notes);
      if (parseFloat(opts.old_due) > 0) row('Old Due Balance', fmtPdf(opts.old_due), red);
      if (parseFloat(opts.due_amount) > 0) row('Due Amount', fmtPdf(opts.due_amount), red);
      divider();

      // Amount paid box
      const boxH = 16;
      doc.setFillColor(232, 245, 233);
      doc.roundedRect(lx, y, W - margin * 2, boxH, 2, 2, 'F');
      doc.setDrawColor(...green);
      doc.roundedRect(lx, y, W - margin * 2, boxH, 2, 2, 'S');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...grey);
      doc.text('Amount Paid', xOff + W / 2, y + 5.5, { align: 'center' });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(...green);
      doc.text(fmtPdf(opts.amount), xOff + W / 2, y + 12, { align: 'center' });
      y += boxH + 4;

      // Footer
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7);
      doc.setTextColor(...grey);
      doc.text(`Thank you! — ${schoolName}`, xOff + W / 2, y + 2, { align: 'center' });

      // QR Code — links to Parent App login, centred
      if (parentQrDataUrl) {
        const qrSize = 22;
        const qrX = xOff + W / 2 - qrSize / 2;
        const qrY = y + 6;
        doc.addImage(parentQrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(5.5);
        doc.setTextColor(120, 120, 120);
        doc.text('Scan for Parent App', xOff + W / 2, qrY + qrSize + 3, { align: 'center' });
      }

      // Signature of Incharge
      const sigLineW = 42;
      const sigX2 = xOff + W - margin;
      const sigX1 = sigX2 - sigLineW;
      const sigY = PH - 14;
      doc.setDrawColor(120, 120, 120);
      doc.line(sigX1, sigY, sigX2, sigY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...grey);
      doc.text('Signature of Incharge', (sigX1 + sigX2) / 2, sigY + 4, { align: 'center' });
    };

    // Left copy — Management
    drawCopy(0, 'MANAGEMENT COPY');

    // Dashed vertical cut line in the middle
    doc.setDrawColor(180, 180, 180);
    doc.setLineDash([2, 2], 0);
    doc.line(half, 4, half, PH - 4);
    doc.setLineDash([], 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(180, 180, 180);
    doc.text('\u2702 Cut', half, PH / 2, { align: 'center' });

    // Right copy — Student
    drawCopy(half + 1, 'STUDENT COPY');

    return doc;
  };

  // Helper: generate QR code data URL for progress card link
  const getQrDataUrl = (studentId) => {
    const sc = sessionStorage.getItem('gs_school_code');
    const url = `${window.location.origin}/student-info/${encodeURIComponent(sc)}/${studentId}`;
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 200, 200);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
    });
  };

  // Helper: generate QR code data URL for the Parent App login (same link as Dashboard)
  const getParentAppQrDataUrl = () => {
    const sc = sessionStorage.getItem('gs_school_code');
    const url = `${window.location.origin}/parent/login?school=${encodeURIComponent(sc)}`;
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 200, 200);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
    });
  };

  const getLogoDataUrl = useCallback(() => {
    if (!effectiveLogoUrl) return Promise.resolve(null);
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const maxSize = 240;
          const scale = Math.min(maxSize / img.naturalWidth, maxSize / img.naturalHeight, 1);
          const width = Math.max(1, Math.round(img.naturalWidth * scale));
          const height = Math.max(1, Math.round(img.naturalHeight * scale));
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = effectiveLogoUrl;
    });
  }, [effectiveLogoUrl]);

  const downloadReceiptPDF = async () => {
    const [qrDataUrl, logoDataUrl, parentQrDataUrl] = await Promise.all([
      getQrDataUrl(student.student_id),
      getLogoDataUrl(),
      getParentAppQrDataUrl(),
    ]);
    const doc = buildReceiptPDF({
      student_name:  student.student_name,
      father_name:   student.father_name,
      mobile_number: student.mobile_number,
      class:         student.class,
      village:       student.village,
      admission_no:  student.admission_no,
      payment_date:  lastPayment.payment_date,
      receipt_no:    receiptNo,
      payment_type:  lastPayment.payment_type,
      month_year:    lastPayment.month_year,
      term:          lastPayment.term,
      notes:         lastPayment.notes,
      amount:        lastPayment.amount,
      old_due:       student.old_due_amount,
      due_amount:    receiptDueAmount,
      payment_mode:  lastPayment.payment_mode,
    }, qrDataUrl, logoDataUrl, parentQrDataUrl);
    const filename = `receipt_${receiptNo || student.student_name.replace(/\s+/g, '_')}_${lastPayment.payment_date}.pdf`;
    doc.save(filename);
  };

  const dueAtPayment = (p) => {
    // totalDueAmount is already correctly calculated (current pending).
    // Add back all payments that came AFTER this one to get the due at this payment's moment.
    const sorted = [...payments].sort((a, b) => {
      const dateDiff = new Date(a.payment_date) - new Date(b.payment_date);
      return dateDiff !== 0 ? dateDiff : a.payment_id - b.payment_id;
    });
    const idx = sorted.findIndex((x) => x.payment_id === p.payment_id);
    const paidAfter = sorted.slice(idx + 1).reduce((s, x) => s + parseFloat(x.amount || 0), 0);
    return Math.max(0, totalDueAmount + paidAfter);
  };

  const downloadHistoryPDF = async (p) => {
    const [qrDataUrl, logoDataUrl, parentQrDataUrl] = await Promise.all([
      getQrDataUrl(student.student_id),
      getLogoDataUrl(),
      getParentAppQrDataUrl(),
    ]);
    const doc = buildReceiptPDF({
      student_name:  student.student_name,
      father_name:   student.father_name,
      mobile_number: student.mobile_number,
      class:         p.class_at_payment || student.class,
      village:       student.village,
      admission_no:  student.admission_no,
      payment_date:  p.payment_date,
      receipt_no:    p.receipt_no,
      payment_type:  p.payment_type,
      month_year:    p.month_year,
      term:          p.term,
      notes:         p.notes,
      amount:        p.amount,
      old_due:       student.old_due_amount,
      due_amount:    dueAtPayment(p),
      payment_mode:  p.payment_method === 'razorpay' ? 'Online (Razorpay)' : p.payment_mode,
      transaction_id: p.razorpay_payment_id || undefined,
    }, qrDataUrl, logoDataUrl, parentQrDataUrl);
    const filename = `receipt_${p.receipt_no || p.payment_id}_${p.payment_date}.pdf`;
    doc.save(filename);
  };

  // ── Year-end Fee Statement PDF ──────────────────────────────────────────────
  const downloadStatementPDF = async () => {
    const logoDataUrl = await getLogoDataUrl();
    const doc  = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const W    = doc.internal.pageSize.getWidth();
    const blue = [13, 36, 68];
    const grey = [102, 102, 102];
    const hasLogo = Boolean(logoDataUrl);
    const headerHeight = hasLogo ? 28 : 24;
    const logoSize = 7;
    const logoY = 2;
    const schoolNameY = hasLogo ? logoY + logoSize + 8 : 10;
    const schoolSubY = hasLogo ? schoolNameY + 6 : 17;

    // Header bar
    doc.setFillColor(...blue);
    doc.rect(0, 0, W, headerHeight, 'F');
    if (hasLogo) {
      doc.addImage(logoDataUrl, 'PNG', W / 2 - logoSize / 2, logoY, logoSize, logoSize);
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text(schoolName.toUpperCase(), W / 2, schoolNameY, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`${schoolLocation} | Fee Management`, W / 2, schoolSubY, { align: 'center' });

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...blue);
    doc.text(`FEE STATEMENT — ${year}`, W / 2, 32, { align: 'center' });

    // Student info block
    let y = 40;
    const info = (label, value) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...grey);
      doc.text(label + ':', 14, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(String(value || '—'), 55, y);
      y += 6;
    };
    info('Student Name',  student.student_name);
    info('Father Name',   student.father_name);
    info('Class',         student.class);
    info('Village',       student.village);

    // Divider
    y += 2;
    doc.setDrawColor(200, 200, 200);
    doc.line(14, y, W - 14, y);
    y += 6;

    // Table header
    const cols = [14, 50, 80, 115, 140, 175];
    doc.setFillColor(230, 235, 245);
    doc.rect(14, y - 4, W - 28, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...blue);
    doc.text('#',           cols[0], y);
    doc.text('Date',        cols[1], y);
    doc.text('Receipt No',  cols[2], y);
    doc.text('Type/Period', cols[3], y);
    doc.text('Notes',       cols[4], y);
    doc.text('Amount',      cols[5], y, { align: 'right' });
    y += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    let total = 0;
    payments.forEach((p, i) => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.setTextColor(50, 50, 50);
      const period = p.month_year || p.term || '';
      const typeStr = paymentLabel(p.payment_type) + (period ? ` – ${period}` : '');
      doc.text(String(i + 1),                              cols[0], y);
      doc.text(new Date(p.payment_date).toLocaleDateString('en-IN'), cols[1], y);
      doc.text(p.receipt_no || '—',                        cols[2], y);
      doc.text(typeStr.length > 22 ? typeStr.slice(0, 22) + '…' : typeStr, cols[3], y);
      doc.text((p.notes || '').slice(0, 14),               cols[4], y);
      doc.setFont('helvetica', 'bold');
      doc.text(fmtPdf(p.amount),                           cols[5], y, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      total += parseFloat(p.amount || 0);
      // Light row separator
      doc.setDrawColor(230, 230, 230);
      doc.line(14, y + 2, W - 14, y + 2);
      y += 8;
    });

    // Total row
    y += 2;
    doc.setFillColor(232, 245, 233);
    doc.rect(14, y - 5, W - 28, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(30, 126, 52);
    doc.text('Total Paid', 14, y);
    doc.text(fmtPdf(total), cols[5], y, { align: 'right' });

    // Pending balance
    if (pendingAmount > 0) {
      y += 8;
      doc.setTextColor(180, 30, 30);
      doc.text('Balance Due', 14, y);
      doc.text(fmtPdf(pendingAmount), cols[5], y, { align: 'right' });
    }

    // Footer
    const H = doc.internal.pageSize.getHeight();
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(...grey);
    doc.text(`Generated on ${new Date().toLocaleDateString('en-IN')} — ${schoolName} Management`, W / 2, H - 8, { align: 'center' });

    doc.save(`statement_${student.student_name.replace(/\s+/g, '_')}_${year}.pdf`);
  };

  const buildTwoUpReceiptHtml = (receiptHtml, copyLabels = ['MANAGEMENT COPY', 'STUDENT COPY']) => `
    <html><head><title>Fee Receipt</title>
    <style>
      @page { size: A4 landscape; margin: 8mm; }
      * { box-sizing: border-box; }
      body { font-family: 'Segoe UI', sans-serif; margin: 0; padding: 0; color: #333; background: #fff; }
      .page { display: flex; flex-direction: row; width: 100%; height: 100vh; }
      .copy { flex: 1; padding: 12px 14px; border: 1.5px solid #0d2444; border-radius: 8px; position: relative; overflow: hidden; }
      .copy + .copy { margin-left: 0; border-left: none; border-radius: 0 8px 8px 0; }
      .copy:first-child { border-radius: 8px 0 0 8px; }
      .cut-line { width: 1px; background: repeating-linear-gradient(to bottom, #bbb 0, #bbb 4px, transparent 4px, transparent 8px); flex-shrink: 0; position: relative; display: flex; align-items: center; justify-content: center; }
      .cut-label { writing-mode: vertical-rl; font-size: 8px; color: #bbb; letter-spacing: 2px; }
      .copy-label { position: absolute; top: 6px; right: 10px; font-size: 8px; color: #aaa; font-weight: 600; letter-spacing: .5px; }
      .school-name { text-align: center; font-size: 1.1rem; font-weight: 800; color: #0d2444; margin-top: 18px; }
      .school-sub  { text-align: center; font-size: .75rem; color: #666; margin-bottom: 10px; }
      .divider { border: none; border-top: 1px dashed #ccc; margin: 8px 0; }
      .receipt-title { text-align: center; font-size: .85rem; font-weight: 700; background: #0d2444; color: #fff; padding: 4px 10px; border-radius: 5px; margin-bottom: 10px; }
      .row { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: .82rem; }
      .label { color: #666; }
      .value { font-weight: 600; }
      .amount-box { text-align: center; background: #e8f5e9; border: 1px solid #34a853; border-radius: 8px; padding: 8px; margin: 10px 0; }
      .amount-box .amt { font-size: 1.3rem; font-weight: 800; color: #1e7e34; }
      .footer { text-align: center; font-size: .7rem; color: #999; margin-top: 10px; }
      img { display: block; max-width: 60px; max-height: 60px; object-fit: contain; margin: 0 auto 6px; }
      canvas, svg { display: block; margin: 0 auto; }
    </style></head>
    <body>
      <div class="page">
        <div class="copy">
          <span class="copy-label">${copyLabels[0]}</span>
          ${receiptHtml}
        </div>
        <div class="cut-line"><span class="cut-label">CUT</span></div>
        <div class="copy">
          <span class="copy-label">${copyLabels[1]}</span>
          ${receiptHtml}
        </div>
      </div>
    </body></html>
  `;

  const handlePrint = () => {
    const content = receiptRef.current.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(buildTwoUpReceiptHtml(content));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  const printSingleReceipt = (p) => {
    const schoolCode = sessionStorage.getItem('gs_school_code');
    const parentUrl = `${window.location.origin}/parent/login?school=${encodeURIComponent(schoolCode)}`;
    const receiptBody = `
      ${receiptLogoHtml}
      <div class="school-name">🏫 ${escHtml(schoolName.toUpperCase())}</div>
      <div class="school-sub">${escHtml(schoolLocation)} | Fee Management</div>
      <hr class="divider" />
      <div class="receipt-title">FEE RECEIPT</div>
      <div class="row"><span class="label">Student Name</span><span class="value">${escHtml(student.student_name)}</span></div>
      <div class="row"><span class="label">Father Name</span><span class="value">${escHtml(student.father_name || '—')}</span></div>
      ${student.mobile_number ? `<div class="row"><span class="label">Parent Mobile</span><span class="value">${escHtml(student.mobile_number)}</span></div>` : ''}
      <div class="row"><span class="label">Class</span><span class="value">${escHtml(p.class_at_payment || student.class)}</span></div>
      <div class="row"><span class="label">Village</span><span class="value">${escHtml(student.village)}</span></div>
      ${student.admission_no ? `<div class="row"><span class="label">Admission No</span><span class="value">${escHtml(student.admission_no)}</span></div>` : ''}
      <hr class="divider" />
      <div class="row"><span class="label">Payment Date</span><span class="value">${new Date(p.payment_date).toLocaleDateString('en-IN')}</span></div>
      ${p.receipt_no ? `<div class="row"><span class="label">Receipt No</span><span class="value" style="font-family:monospace;color:#1a56db;">${escHtml(p.receipt_no)}</span></div>` : ''}
      <div class="row"><span class="label">Payment Type</span><span class="value">${escHtml(paymentLabel(p.payment_type))}</span></div>
      ${p.payment_method === 'razorpay' ? `<div class="row"><span class="label">Payment Mode</span><span class="value">Online (Razorpay)</span></div>` : (p.payment_mode ? `<div class="row"><span class="label">Payment Mode</span><span class="value">${escHtml(p.payment_mode)}</span></div>` : '')}
      ${p.razorpay_payment_id ? `<div class="row"><span class="label">Transaction ID</span><span class="value" style="font-family:monospace;font-size:.72rem;">${escHtml(p.razorpay_payment_id)}</span></div>` : ''}
      ${p.month_year ? `<div class="row"><span class="label">Period</span><span class="value">${escHtml(p.month_year)}</span></div>` : ''}
      ${p.term ? `<div class="row"><span class="label">Term</span><span class="value">${escHtml(p.term)}</span></div>` : ''}
      ${p.notes ? `<div class="row"><span class="label">Notes</span><span class="value">${escHtml(p.notes)}</span></div>` : ''}
      ${dueAtPayment(p) > 0 ? `<div class="row"><span class="label">Due Amount</span><span class="value" style="color:#b91c1c;">${escHtml(fmt(dueAtPayment(p)))}</span></div>` : ''}
      <div class="amount-box">
        <div style="font-size:.8rem;color:#555;">Amount Paid</div>
        <div class="amt">${fmt(p.amount)}</div>
      </div>
      <div style="display:flex;justify-content:center;margin-top:10px;">
        <div style="text-align:center;">
          <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(parentUrl)}" style="width:80px;height:80px;margin:0 auto;" />
          <div style="font-size:.65rem;color:#666;">Scan for Parent App</div>
        </div>
      </div>
      <hr class="divider" />
      <div class="footer">Thank you! — ${escHtml(schoolName)} Management</div>
      <div style="display:flex;justify-content:flex-end;margin-top:24px;">
        <div style="text-align:center;">
          <div style="border-top:1px solid #333;width:140px;margin-bottom:4px;"></div>
          <div style="font-size:.7rem;color:#555;">Signature of Incharge</div>
        </div>
      </div>
    `;
    const win = window.open('', '_blank');
    win.document.write(buildTwoUpReceiptHtml(receiptBody));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  return createPortal(
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box large">
        {/* Fully Paid Warning */}
        {fullyPaid && (
          <div style={{
            background: '#fee2e2',
            border: '2px solid #dc2626',
            borderRadius: 8,
            padding: '14px 16px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <span style={{fontSize: '1.5rem'}}>🚫</span>
            <div>
              <strong style={{color: '#991b1b'}}>Cannot Process Payment</strong>
              <p style={{margin: '4px 0 0 0', color: '#7f1d1d', fontSize: '.9rem'}}>
                {FULLY_PAID_MESSAGE}
              </p>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2>💳 Payments — {student.student_name}</h2>
            <p className="modal-subtitle">
              Class: <strong>{student.class}</strong> &nbsp;|&nbsp;
              Village: <strong>{student.village}</strong> &nbsp;|&nbsp;
              Pending:{' '}
              <strong className={pendingAmount > 0 ? 'text-red' : 'text-green'}>
                {fullyPaid ? 'Fully Paid' : fmt(pendingAmount)}
              </strong>
              {(() => {
                const customPending = multiSelections.filter((s) => s.isCustom).reduce((sum, s) => sum + (s.pending !== null && s.pending > 0 ? s.pending : 0), 0);
                return customPending > 0 ? (
                  <>
                    &nbsp;|&nbsp;
                    Custom Fee Pending:{' '}
                    <strong className="text-red">{fmt(customPending)}</strong>
                  </>
                ) : null;
              })()}
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Tabs */}
        <div className="tabs">
          {!historyOnly && (
            <button
              className={`tab ${activeTab === 'add' ? 'active' : ''}`}
              onClick={() => {
                if (fullyPaid) {
                  alert(FULLY_PAID_MESSAGE);
                  return;
                }
                setActiveTab('add');
              }}
            >
              + Record Payment
            </button>
          )}
          <button className={`tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
            History ({payments.length})
          </button>
          {!historyOnly && (
            <button className={`tab ${activeTab === 'installments' ? 'active' : ''}`} onClick={() => setActiveTab('installments')}>
              {`\u{1F4C5} Installments (${installments.length})`}
            </button>
          )}
          {!historyOnly && (
            <button
              className={`tab ${activeTab === 'multi' ? 'active' : ''}`}
              onClick={() => {
                if (fullyPaid) {
                  alert(FULLY_PAID_MESSAGE);
                  return;
                }
                setMultiDone(null);
                setMultiError(null);
                setActiveTab('multi');
              }}
            >
              {`\u{1F4B0} Custom Fee Payment`}
            </button>
          )}
          {!historyOnly && lastPayment && (
            <button className={`tab ${activeTab === 'receipt' ? 'active' : ''}`} onClick={() => setActiveTab('receipt')}>
              {`\u{1F9FE} Receipt`}
            </button>
          )}
        </div>

        {/* ── Add Payment ── */}
        {activeTab === 'add' && (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="error-banner">{error}</div>}

              {/* Per-fee-type breakdown card */}
              {(() => {
                const customRows = feeTypes
                  .filter((ft) => parseFloat(ft.fee_amount || 0) > 0)
                  .map((ft) => {
                    const paid = payments.reduce((s, p) => p.payment_type === ft.fee_name ? s + parseFloat(p.amount || 0) : s, 0);
                    return { label: ft.fee_name, fee: parseFloat(ft.fee_amount), paid, type: ft.fee_name };
                  });
                const hasBusTerms = _hasBusTerms;
                const busRows = hasBusTerms
                  ? [
                      { label: 'Term 1 Transport Fee', fee: _busTerm1Fee, paid: _effectivePaidBusTerm1, type: 'bus_term1' },
                      { label: 'Term 2 Transport Fee', fee: _busTerm2Fee, paid: _effectivePaidBusTerm2, type: 'bus_term2' },
                    ]
                  : [{ label: 'Transport Fee', fee: parseFloat(student.bus_fee || 0), paid: paidFromHistory.bus, type: 'bus' }];
                const tuitionRows = _hasTuiTerms
                  ? [
                      ...(_tuiTerm1Fee > 0 ? [{ label: 'Term 1 Tuition Fee', fee: _tuiTerm1Fee, paid: _effectivePaidTuiTerm1, type: 'tuition_term1' }] : []),
                      ...(_tuiTerm2Fee > 0 ? [{ label: 'Term 2 Tuition Fee', fee: _tuiTerm2Fee, paid: _effectivePaidTuiTerm2, type: 'tuition_term2' }] : []),
                      ...(_tuiTerm3Fee > 0 ? [{ label: 'Term 3 Tuition Fee', fee: _tuiTerm3Fee, paid: _effectivePaidTuiTerm3, type: 'tuition_term3' }] : []),
                    ]
                  : [{ label: 'Tuition Fee', fee: parseFloat(student.total_fee || 0), paid: paidFromHistory.tuition }];
                const hostelFeeAmt  = parseFloat(student.hostel_fee || 0);
                const hostelT1Fee   = parseFloat(student.hostel_fee_term1 || 0) || derivedHostelTerms.term1;
                const hostelT2Fee   = parseFloat(student.hostel_fee_term2 || 0) || derivedHostelTerms.term2;
                const hasHTerms     = hostelT1Fee > 0 || hostelT2Fee > 0;
                // Distribute legacy 'hostel' generic payments into term buckets for display
                let _hgPd = Math.max(0, (paidFromHistory.hostel || 0) - (paidFromHistory.hostel_term1 || 0) - (paidFromHistory.hostel_term2 || 0));
                let _ht1Pd = paidFromHistory.hostel_term1 || 0;
                let _ht2Pd = paidFromHistory.hostel_term2 || 0;
                if (hasHTerms && _hgPd > 0) {
                  const _ht1Ex = Math.min(_hgPd, Math.max(0, hostelT1Fee - _ht1Pd));
                  _hgPd -= _ht1Ex;
                  _ht1Pd += _ht1Ex;
                  _ht2Pd += _hgPd;
                }
                const hostelRows = hasHTerms
                  ? [
                      ...(hostelT1Fee > 0 ? [{ label: 'Term 1 Hostel Fee', fee: hostelT1Fee, paid: _ht1Pd, type: 'hostel_term1' }] : []),
                      ...(hostelT2Fee > 0 ? [{ label: 'Term 2 Hostel Fee', fee: hostelT2Fee, paid: _ht2Pd, type: 'hostel_term2' }] : []),
                    ]
                  : (hostelFeeAmt > 0 || paidFromHistory.hostel > 0
                      ? [{ label: 'Hostel Fee', fee: hostelFeeAmt, paid: paidFromHistory.hostel, type: 'hostel' }]
                      : []);
                const rows = [
                  ...tuitionRows,
                  ...busRows,
                  ...hostelRows,
                  { label: 'Uniform Fee', fee: parseFloat(student.uniform_fee  || 0), paid: paidFromHistory.uniform, type: 'uniform' },
                  { label: 'Books Fee',   fee: parseFloat(student.books_fee    || 0), paid: paidFromHistory.books,   type: 'books'   },
                  ...customRows,
                ].filter((r) => r.fee > 0 || r.paid > 0);
                if (rows.length === 0) return null;
                return (
                  <div className="fee-breakdown-card">
                    <div className="fbc-header">📊 Fee Breakdown</div>
                    <table className="fbc-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Charged</th>
                          <th>Paid</th>
                          <th>Pending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => {
                          const pending   = Math.max(0, r.fee - r.paid);
                          const fullPaid  = r.type && r.fee > 0 && pending <= 0;
                          const clickable = r.type && !fullPaid;
                          return (
                            <tr
                              key={r.label}
                              className={form.payment_type === r.type ? 'fbc-active' : fullPaid ? 'fbc-done' : ''}
                              onClick={() => clickable && setForm((p) => ({ ...p, payment_type: r.type }))}
                              style={clickable ? { cursor: 'pointer' } : {}}
                              title={fullPaid ? 'Already fully paid' : r.type ? 'Click to select this fee type' : ''}
                            >
                              <td><strong>{r.label}</strong>{fullPaid && <span className="fbc-paid-badge">✅ Paid</span>}</td>
                              <td>{fmt(r.fee)}</td>
                              <td className="text-green">{fmt(r.paid)}</td>
                              <td className={pending > 0 ? 'text-red' : 'text-green'}>{pending > 0 ? fmt(pending) : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              <div className="form-grid">
                <div className="form-group">
                  <label>Payment Type</label>
                  <select name="payment_type" value={form.payment_type} onChange={handleChange}>
                    <optgroup label="Tuition Fee">
                      <option value="monthly">Monthly Tuition</option>
                      <option value="term">Term-wise Tuition</option>
                      {_hasTuiTerms && _tuiTerm1Fee > 0 && <option value="tuition_term1">Tuition – Term 1</option>}
                      {_hasTuiTerms && _tuiTerm2Fee > 0 && <option value="tuition_term2">Tuition – Term 2</option>}
                      {_hasTuiTerms && _tuiTerm3Fee > 0 && <option value="tuition_term3">Tuition – Term 3</option>}
                    </optgroup>
                    {(parseFloat(student.bus_fee_term1 || 0) > 0 || parseFloat(student.bus_fee_term2 || 0) > 0) ? (
                      <optgroup label="Transport Fee">
                        <option value="bus_term1">Transport Fee – Term 1</option>
                        <option value="bus_term2">Transport Fee – Term 2</option>
                      </optgroup>
                    ) : (
                      <optgroup label="Transport Fee">
                        <option value="bus">Transport Fee</option>
                      </optgroup>
                    )}
                    <optgroup label="Other Fees">
                      <option value="uniform">Uniform Fee</option>
                      <option value="books">Books Fee</option>
                      <option value="other">Fee (Enter Amount Manually)</option>
                    </optgroup>
                    {parseFloat(student.hostel_fee || 0) > 0 && (
                      <optgroup label="Hostel Fee">
                        {((parseFloat(student.hostel_fee_term1 || 0) || derivedHostelTerms.term1) > 0 || (parseFloat(student.hostel_fee_term2 || 0) || derivedHostelTerms.term2) > 0) ? (
                          <>
                            {(parseFloat(student.hostel_fee_term1 || 0) || derivedHostelTerms.term1) > 0 && <option value="hostel_term1">Hostel Fee – Term 1</option>}
                            {(parseFloat(student.hostel_fee_term2 || 0) || derivedHostelTerms.term2) > 0 && <option value="hostel_term2">Hostel Fee – Term 2</option>}
                          </>
                        ) : (
                          <option value="hostel">Hostel Fee</option>
                        )}
                      </optgroup>
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Amount (₹) <span className="required">*</span>
                    {form.payment_type !== 'monthly' && maxAllowed !== null && maxAllowed > 0 && (
                      <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.8rem', marginLeft: 6 }}>
                        (max ₹{maxAllowed.toLocaleString('en-IN')})
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    required
                    min="1"
                    step="0.01"
                    max={maxAllowed !== null ? maxAllowed : undefined}
                    placeholder={maxAllowed !== null && maxAllowed > 0 ? `Max ₹${maxAllowed.toLocaleString('en-IN')}` : 'Enter amount'}
                  />
                </div>

                <div className="form-group">
                  <label>Payment Date <span className="required">*</span></label>
                  <input
                    type="date"
                    name="payment_date"
                    value={form.payment_date}
                    onChange={handleChange}
                    required
                    lang="en-GB"
                  />
                </div>

                <div className="form-group">
                  <label>Payment Mode</label>
                  <select
                    name="payment_mode"
                    value={form.payment_mode}
                    onChange={handleChange}
                  >
                    <option value="">— Select Mode —</option>
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="Debit Card">Debit Card</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Net Banking">Net Banking</option>
                    <option value="Others">Others</option>
                  </select>
                </div>

                {form.payment_type === 'monthly' ? (
                  <div className="form-group">
                    <label>Month / Year</label>
                    <input
                      type="text"
                      name="month_year"
                      value={form.month_year}
                      onChange={handleChange}
                      placeholder="e.g. Jan 2025"
                      maxLength={20}
                    />
                  </div>
                ) : form.payment_type === 'term' ? (
                  <div className="form-group">
                    <label>Term</label>
                    <input
                      type="text"
                      name="term"
                      value={form.term}
                      onChange={handleChange}
                      placeholder="e.g. Term 1, Q2 2025"
                      maxLength={50}
                    />
                  </div>
                ) : null}

                <div className="form-group full-width">
                  <label>Notes</label>
                  <input
                    type="text"
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    placeholder="Optional notes"
                    maxLength={500}
                  />
                </div>

                {remainingOldDue > 0 && form.amount && parseFloat(form.amount) > 0 && parseFloat(form.amount) < remainingOldDue && (() => {
                  const entered  = parseFloat(form.amount);
                  const oldPart  = Math.min(entered, remainingOldDue);
                  const newPart  = entered - oldPart;
                  return (
                    <div className="form-group full-width">
                      <div className="old-due-priority-box">
                        <div className="odp-header">⚠️ Old Due Priority</div>
                        <div className="odp-remaining">Old due remaining: <strong>{fmt(remainingOldDue)}</strong></div>
                        <div className="odp-split">
                          <span>→ <strong>{fmt(oldPart)}</strong> clears old due</span>
                          {newPart > 0
                            ? <span>→ <strong>{fmt(newPart)}</strong> towards current year</span>
                            : <span className="odp-note">Current year fee unchanged</span>}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={fullyPaid || loading}>
                {loading ? 'Saving…' : fullyPaid ? 'Payment Not Allowed' : 'Record Payment'}
              </button>
            </div>
          </form>
        )}

        {/* ── Payment History ── */}
        {activeTab === 'history' && (
          <div className="modal-body">
            {payments.length === 0 ? (
              <p className="no-data">No payment records yet.</p>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Receipt No</th>
                        <th>Type</th>
                        <th>Period</th>
                        <th>Amount</th>
                        <th>Notes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.payment_id}>
                          <td>{new Date(p.payment_date).toLocaleDateString('en-IN')}</td>
                          <td style={{fontFamily:'monospace',fontSize:'.82rem',color:'#1a56db'}}>
                            {p.receipt_no || '—'}
                            {p.payment_method === 'razorpay' && (
                              <span
                                title={p.razorpay_payment_id ? `Paid online via Razorpay — Txn ID: ${p.razorpay_payment_id}` : 'Paid online via Razorpay'}
                                style={{
                                  display: 'inline-block', marginLeft: 6, fontFamily: 'sans-serif',
                                  fontSize: '.68rem', fontWeight: 600, color: '#0d6efd',
                                  background: '#e7f1ff', border: '1px solid #b6d4fe',
                                  borderRadius: 10, padding: '1px 7px', verticalAlign: 'middle',
                                }}
                              >
                                Online
                              </span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${p.payment_type}`}>
                              {p.payment_type === 'old_due' ? 'Old Due' : p.payment_type}
                            </span>
                          </td>
                          <td>{p.month_year || p.term || '—'}</td>
                          <td className="text-green">{fmt(p.amount)}</td>
                          <td>{p.notes || '—'}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => printSingleReceipt(p)}
                              title="Print receipt"
                            >
                              🖨️
                            </button>
                            <button
                              className="btn btn-sm btn-pdf"
                              onClick={() => downloadHistoryPDF(p)}
                              title="Download PDF receipt"
                              style={{ marginLeft: 4 }}
                            >
                              📅 PDF
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4"><strong>Total Paid (this history)</strong></td>
                        <td className="text-green"><strong>{fmt(totalPaid)}</strong></td>
                        <td colSpan="2" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
              {payments.length > 0 && (
                <button className="btn btn-pdf" onClick={downloadStatementPDF} title="Download full year fee statement">
                  📋 Statement PDF
                </button>
              )}
            </div>
          </div>
        )}
        {activeTab === 'installments' && (
          <div className="modal-body">
            <p style={{ color: '#666', marginBottom: 12 }}>
              Define expected installments for <strong>{year}</strong>. Match the term name with your payment term to track payment status.
            </p>
            {/* Add / Edit form */}
            <form onSubmit={handleInstSubmit} style={{ marginBottom: 16 }}>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr auto' }}>
                <div className="form-group">
                  <label>Term Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="term_name"
                    value={instForm.term_name}
                    onChange={handleInstChange}
                    required
                    placeholder="e.g. Term 1"
                    maxLength={50}
                  />
                </div>
                <div className="form-group">
                  <label>Expected Amount (₹) <span className="required">*</span></label>
                  <input
                    type="number"
                    name="expected_amount"
                    value={instForm.expected_amount}
                    onChange={handleInstChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    name="due_date"
                    value={instForm.due_date}
                    onChange={handleInstChange}
                    lang="en-GB"
                  />
                </div>
                <div className="form-group" style={{ alignSelf: 'flex-end' }}>
                  <button type="submit" className="btn btn-primary" disabled={instLoading}>
                    {instLoading ? '…' : editInstId ? 'Update' : '+ Add'}
                  </button>
                  {editInstId && (
                    <button type="button" className="btn btn-secondary" style={{ marginLeft: 4 }}
                      onClick={() => { setEditInstId(null); setInstForm({ term_name: '', expected_amount: '', due_date: '' }); }}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </form>

            {installments.length === 0 ? (
              <p className="no-data">No installments defined yet. Add terms above.</p>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Term</th>
                      <th>Due Date</th>
                      <th>Expected</th>
                      <th>Paid (matching term)</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {installments.map((inst) => {
                      const paid      = paidByTerm[inst.term_name] || 0;
                      const balance   = parseFloat(inst.expected_amount) - paid;
                      const status    = balance <= 0 ? 'Paid ✅' : paid > 0 ? 'Partial ⚠️' : 'Pending ❌';
                      const cls       = balance <= 0 ? 'text-green' : paid > 0 ? 'text-orange' : 'text-red';
                      const isOverdue = balance > 0 && inst.due_date && new Date(inst.due_date) < new Date();
                      return (
                        <tr key={inst.installment_id} className={isOverdue ? 'overdue-inst-row' : ''}>
                          <td>
                            <strong>{inst.term_name}</strong>
                            {isOverdue && <span className="overdue-badge" title="Past due date and unpaid">⚠️ Overdue</span>}
                          </td>
                          <td>{inst.due_date ? new Date(inst.due_date).toLocaleDateString('en-IN') : '—'}</td>
                          <td>{fmt(inst.expected_amount)}</td>
                          <td className="text-green">{fmt(paid)}</td>
                          <td className={cls}>{fmt(Math.max(0, balance))}</td>
                          <td><span className={cls}>{status}</span></td>
                          <td>
                            <button className="btn btn-sm btn-secondary" onClick={() => startEditInst(inst)} title="Edit">✏️</button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleDeleteInst(inst.installment_id)} title="Delete" style={{ marginLeft: 4 }}>🗑️</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="2"><strong>Total</strong></td>
                      <td><strong>{fmt(installments.reduce((s, i) => s + parseFloat(i.expected_amount), 0))}</strong></td>
                      <td className="text-green"><strong>{fmt(Object.values(paidByTerm).reduce((s, v) => s + v, 0))}</strong></td>
                      <td colSpan="3" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        )}

        {/* ── Bulk Pay ── */}
        {activeTab === 'multi' && (
          <form onSubmit={handleMultiSubmit}>
            <div className="modal-body">
              {multiError && <div className="error-banner">{multiError}</div>}

              {multiDone ? (
                <div className="multi-pay-success">
                  <div className="mps-header">✅ Payments Recorded Successfully</div>
                  <table className="mps-table">
                    <thead><tr><th>Fee Type</th><th>Amount</th><th>Receipt No</th></tr></thead>
                    <tbody>
                      {multiDone.map((r, i) => (
                        <tr key={i}>
                          <td>{r.type}</td>
                          <td className="text-green">{fmt(r.amount)}</td>
                          <td style={{ fontFamily: 'monospace', color: '#1a56db' }}>{r.receipt_no || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><strong>Total</strong></td>
                        <td className="text-green"><strong>{fmt(multiDone.reduce((s, r) => s + parseFloat(r.amount), 0))}</strong></td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <>
                  <div className="multi-pay-list">
                    {multiSelections.filter((s) => s.isCustom).length === 0 && <p className="no-data">No custom fees configured for this school.</p>}
                    {multiSelections.filter((s) => s.isCustom).map((s) => {
                      const fullyPaid = s.pending !== null && s.pending <= 0;
                      return (
                        <div key={s.id} className={`mpl-row${s.checked ? ' mpl-checked' : ''}${fullyPaid ? ' mpl-zero' : ''}`}>
                          <label className="mpl-check-label">
                            <input
                              type="checkbox"
                              checked={s.checked}
                              disabled={fullyPaid}
                              onChange={(e) => setMultiSelections((prev) => prev.map((r) => r.id === s.id ? { ...r, checked: e.target.checked } : r))}
                            />
                            <span className="mpl-name">{s.label}</span>
                            {s.isCustom && s.paidSoFar > 0 && <span className="mpl-pending">paid so far: {fmt(s.paidSoFar)}</span>}
                            {!s.isCustom && s.pending > 0 && <span className="mpl-pending">pending: {fmt(s.pending)}</span>}
                            {fullyPaid  && <span className="mpl-paid-tag">✅ Paid</span>}
                            {s.isCustom && <span className="mpl-custom-badge">custom</span>}
                          </label>
                          <div className="mpl-inputs">
                            {s.id === 'tuition' && s.checked && (
                              <select
                                className="mpl-subtype-select"
                                value={s.subType}
                                onChange={(e) => setMultiSelections((prev) => prev.map((r) => r.id === s.id ? { ...r, subType: e.target.value } : r))}
                              >
                                <option value="monthly">Monthly</option>
                                <option value="term">Term</option>
                              </select>
                            )}
                            <input
                              type="number"
                              className="mpl-amount-input"
                              value={s.amount}
                              min="1"
                              step="0.01"
                              max={s.pending !== null && s.pending > 0 ? s.pending : undefined}
                              placeholder="₹ Amount"
                              disabled={!s.checked || fullyPaid}
                              onChange={(e) => setMultiSelections((prev) => prev.map((r) => r.id === s.id ? { ...r, amount: e.target.value } : r))}
                            />
                            {s.id === 'tuition' && s.checked && s.subType === 'monthly' && (
                              <input
                                type="text"
                                className="mpl-extra-input"
                                value={s.month_year || ''}
                                placeholder="Month (e.g. Jan 2025)"
                                maxLength={20}
                                onChange={(e) => setMultiSelections((prev) => prev.map((r) => r.id === s.id ? { ...r, month_year: e.target.value } : r))}
                              />
                            )}
                            {s.id === 'tuition' && s.checked && s.subType === 'term' && (
                              <input
                                type="text"
                                className="mpl-extra-input"
                                value={s.term || ''}
                                placeholder="Term (e.g. Term 1)"
                                maxLength={50}
                                onChange={(e) => setMultiSelections((prev) => prev.map((r) => r.id === s.id ? { ...r, term: e.target.value } : r))}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="form-grid" style={{ marginTop: 16 }}>
                    <div className="form-group">
                      <label>Payment Date <span className="required">*</span></label>
                      <input type="date" value={multiDate} required onChange={(e) => setMultiDate(e.target.value)} lang="en-GB" />
                    </div>
                    <div className="form-group">
                      <label>Notes</label>
                      <input type="text" value={multiNotes} placeholder="Optional notes" maxLength={500} onChange={(e) => setMultiNotes(e.target.value)} />
                    </div>
                  </div>
                  {multiSelections.filter((s) => s.isCustom).some((s) => s.checked && parseFloat(s.amount) > 0) && (
                    <div className="multi-pay-total">
                      Total: <strong>{fmt(multiSelections.filter((s) => s.isCustom && s.checked).reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0))}</strong>
                    </div>
                  )}
                </>
              )}
            </div>
            {!multiDone ? (
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={multiLoading}>
                  {multiLoading ? 'Processing…' : `Pay Selected (${multiSelections.filter((s) => s.isCustom && s.checked && parseFloat(s.amount) > 0).length})`}
                </button>
              </div>
            ) : (
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                <button type="button" className="btn btn-primary" onClick={() => { setMultiDone(null); setMultiError(null); }}>Record More</button>
              </div>
            )}
          </form>
        )}

        {/* ── Receipt ── */}
        {activeTab === 'receipt' && lastPayment && (
          <div className="modal-body">
            <div ref={receiptRef} className="receipt">
              {effectiveLogoUrl ? (
                <img
                  src={effectiveLogoUrl}
                  alt="School Logo"
                  style={receiptLogoStyle}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : null}
              <div className="school-name">🏫 {schoolName.toUpperCase()}</div>
              <div className="school-sub">{schoolLocation} | Fee Management</div>
              <hr className="divider" />
              <div className="receipt-title">FEE RECEIPT</div>
              <div className="row"><span className="label">Student Name</span><span className="value">{student.student_name}</span></div>
              <div className="row"><span className="label">Father Name</span><span className="value">{student.father_name || '—'}</span></div>
              {student.mobile_number && (
                <div className="row"><span className="label">Parent Mobile</span><span className="value">{student.mobile_number}</span></div>
              )}
              <div className="row"><span className="label">Class</span><span className="value">{student.class}</span></div>
              <div className="row"><span className="label">Village</span><span className="value">{student.village}</span></div>
              {student.admission_no && (
                <div className="row"><span className="label">Admission No</span><span className="value">{student.admission_no}</span></div>
              )}
              <hr className="divider" />
              <div className="row"><span className="label">Payment Date</span><span className="value">{new Date(lastPayment.payment_date).toLocaleDateString('en-IN')}</span></div>
              {receiptNo && <div className="row"><span className="label">Receipt No</span><span className="value receipt-no">{receiptNo}</span></div>}
              <div className="row"><span className="label">Payment Type</span><span className="value">{paymentLabel(lastPayment.payment_type)}</span></div>
              {lastPayment.payment_mode && <div className="row"><span className="label">Payment Mode</span><span className="value">{lastPayment.payment_mode}</span></div>}
              {lastPayment.month_year && <div className="row"><span className="label">Period</span><span className="value">{lastPayment.month_year}</span></div>}
              {lastPayment.term      && <div className="row"><span className="label">Term</span><span className="value">{lastPayment.term}</span></div>}
              {lastPayment.notes     && <div className="row"><span className="label">Notes</span><span className="value">{lastPayment.notes}</span></div>}
              {receiptDueAmount > 0 && (
                <div className="row"><span className="label">Due Amount</span><span className="value" style={{ color: '#b91c1c' }}>{fmt(receiptDueAmount)}</span></div>
              )}
              <div className="amount-box">
                <div style={{fontSize:'.8rem',color:'#555'}}>Amount Paid</div>
                <div className="amt">{fmt(lastPayment.amount)}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(`${window.location.origin}/parent/login?school=${encodeURIComponent(sessionStorage.getItem('gs_school_code'))}`)}`}
                    alt="Parent App QR Code"
                    style={{ width: 90, height: 90 }}
                  />
                  <div style={{ fontSize: '0.65rem', color: '#666' }}>Scan for Parent App</div>
                </div>
              </div>
              <hr className="divider" />
              <div className="footer">Thank you! — {schoolName} Management</div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ borderTop: '1px solid #333', width: 140, marginBottom: 4 }} />
                  <div style={{ fontSize: '0.7rem', color: '#555' }}>Signature of Incharge</div>
                </div>
              </div>
            </div>
            {smsStatus && (
              <div style={{ textAlign: 'center', margin: '10px 0 4px' }}>
                {smsStatus.ok
                  ? <span style={{ background: '#dcfce7', color: '#15803d', padding: '4px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>✓ SMS sent to parent</span>
                  : smsStatus.skipped
                    ? <span style={{ background: '#fef9c3', color: '#92400e', padding: '4px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>⚠ SMS {smsStatus.skipped === 'sms_disabled_for_school' ? 'disabled for this school' : smsStatus.skipped === 'server_sms_not_configured' ? 'not configured' : smsStatus.skipped === 'invalid_mobile' ? 'skipped (no valid mobile)' : smsStatus.skipped}</span>
                    : <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '4px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>✗ SMS failed</span>
                }
              </div>
            )}
            <div className="modal-actions" style={{marginTop:'1rem'}}>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
              <button
                className="btn btn-danger"
                onClick={handleCancelReceipt}
                disabled={cancelLoading}
                title="Erase this payment completely and restore the fee balance"
              >
                {cancelLoading ? 'Cancelling…' : '✕ Cancel Payment'}
              </button>
              <button className="btn btn-primary" onClick={handlePrint}>🖨️ Print Receipt</button>
              <button className="btn btn-pdf" onClick={downloadReceiptPDF}>📄 Download PDF</button>
              {student.mobile_number && (
                <button
                  className="btn btn-whatsapp"
                  onClick={async () => {
                    // Build the PDF blob
                    const [qrDataUrl, logoDataUrl, parentQrDataUrl] = await Promise.all([
                      getQrDataUrl(student.student_id),
                      getLogoDataUrl(),
                      getParentAppQrDataUrl(),
                    ]);
                    const doc = buildReceiptPDF({
                      student_name:  student.student_name,
                      father_name:   student.father_name,
                      mobile_number: student.mobile_number,
                      class:         student.class,
                      village:       student.village,
                      admission_no:  student.admission_no,
                      payment_date:  lastPayment.payment_date,
                      receipt_no:    receiptNo,
                      payment_type:  lastPayment.payment_type,
                      month_year:    lastPayment.month_year,
                      term:          lastPayment.term,
                      notes:         lastPayment.notes,
                      amount:        lastPayment.amount,
                      old_due:       student.old_due_amount,
                      due_amount:    receiptDueAmount,
                      payment_mode:  lastPayment.payment_mode,
                    }, qrDataUrl, logoDataUrl, parentQrDataUrl);
                    const pdfBlob = doc.output('blob');
                    const fileName = `Receipt_${receiptNo || student.student_name}_${lastPayment.payment_date}.pdf`;
                    const pdfFile  = new File([pdfBlob], fileName, { type: 'application/pdf' });

                    const period = lastPayment.month_year || lastPayment.term || '';
                    const text = [
                      `🏫 *${schoolName.toUpperCase()} — Fee Receipt*`,
                      receiptNo ? `📌 Receipt No: *${receiptNo}*` : '',
                      `👤 Student: *${student.student_name}*`,
                      `📚 Class: ${student.class} | Village: ${student.village}`,
                      `📅 Date: ${new Date(lastPayment.payment_date).toLocaleDateString('en-IN')}`,
                      period ? `🗓️ Period: ${period}` : '',
                      `💰 Amount Paid: *₹${parseFloat(lastPayment.amount).toLocaleString('en-IN')}*`,
                      `\nThank you! — ${schoolName} Management`,
                    ].filter(Boolean).join('\n');

                    // Try Web Share API with file (works on Android/iOS Chrome)
                    if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
                      try {
                        await navigator.share({ files: [pdfFile], title: 'Fee Receipt', text });
                        return;
                      } catch (e) {
                        if (e.name === 'AbortError') return; // user cancelled
                      }
                    }

                    // Fallback: download PDF + open WhatsApp text
                    doc.save(fileName);
                    const mobile = student.mobile_number.replace(/\D/g, '');
                    const num    = mobile.startsWith('91') ? mobile : `91${mobile}`;
                    setTimeout(() => {
                      window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, '_blank');
                    }, 800);
                  }}
                >
                  📲 WhatsApp PDF
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

